import {
  toTypedRxJsonSchema,
  ExtractDocumentTypeFromTypedRxJsonSchema,
  RxJsonSchema,
  RxDocument,
  RxCollection,
} from 'rxdb'
import IThumbnailResponse from 'src/system/content/models/IThumbnailResponse'

export const resourceSchemaLiteral = {
  title: 'Resource Schema',
  description: 'Describes a resource, which can be a document, iframe, image, link, remote video or video',
  version: 0,
  keyCompression: false,
  primaryKey: {
    key: 'id',
    fields: ['type', 'resourceId'],
    separator: '-',
  },
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 100,
    },
    resourceId: {
      type: 'string',
    },
    type: {
      type: 'string',
      enum: ['document', 'iframe', 'image', 'link', 'remote_video', 'video'],
    },
    folders: {
      ref: 'folders',
      type: 'array',
      items: {
        type: 'string',
      },
    },
    name: {
      type: ['string', 'null'],
    },
    description: {
      type: ['string', 'null'],
    },
    thumbnail: {
      type: ['string', 'null'],
    },
    image: {
      type: ['string', 'null'],
    },
    document: {
      type: ['string', 'null'],
    },
    url: {
      type: ['string', 'null'],
    },
    video: {
      type: ['string', 'null'],
    },
    fileSize: {
      type: ['string', 'null'],
    },
    createdAt: {
      type: ['string', 'null'],
    },
    updatedAt: {
      type: ['string', 'null'],
    },
  },
  required: ['resourceId', 'type'],
  indexes: [],
} as const

const resourceUrlMap = new Map<ResourceType, keyof ResourceDocType>([
  ['document', 'document'],
  ['image', 'image'],
  ['video', 'video'],
  ['iframe', 'url'],
  ['link', 'url'],
  ['remote_video', 'url'],
])

const resourceFileTypeIconMap = new Map<string, string>([
  ['pdf', '/icons/pdf.svg'],
  ['doc', '/icons/doc.svg'],
  ['ppt', '/icons/ppt.svg'],
  ['xls', '/icons/xls.svg'],
  ['png', '/icons/png.svg'],
  ['jpg', '/icons/jpg.svg'],
  ['link', '/icons/link.svg'],
  ['mov', '/icons/mov.svg'],
  ['eps', '/icons/image.svg'],
])

const schemaTyped = toTypedRxJsonSchema(resourceSchemaLiteral)

export type ResourceDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof schemaTyped>

export type ResourceType = ResourceDocType['type']

export const resourceSchema: RxJsonSchema<ResourceDocType> = schemaTyped

export type ResourceDocMethods = {
  isFolder: () => boolean
  isResource: () => boolean
  getThumbnail: () => IThumbnailResponse
  getUrl: () => string | null
  getFileType: () => string
}

export const resourceDocMethods = {
  isFolder: () => false,
  isResource: () => true,
  getThumbnail(): IThumbnailResponse {
    if (this.thumbnail) {
      return {
        maximize: true,
        url: this.thumbnail,
      }
    }

    return {
      maximize: false,
      url: resourceFileTypeIconMap.get(this.getFileType()),
    }
  },
  getUrl(): string | null {
    const property = resourceUrlMap.get(this.type)

    if (typeof property === 'undefined') {
      return null
    }

    return this[property] as string | null
  },
  getFileType(): string {
    //console.debug('getFileType', this.type, this.document, this.image, this.image?.split('.').pop())
    switch (this.type) {
      case 'document':
        switch (this.document?.split('.')?.pop()) {
          case 'pdf':
            return 'pdf'
          case 'docx':
          case 'doc':
          default:
            return 'doc'
          case 'pptx':
          case 'ppt':
            return 'ppt'
          case 'xlsx':
          case 'xls':
            return 'xls'
        }
      case 'image':
        return this.image?.split('.').pop() || 'image'
      case 'iframe':
      case 'link':
        return 'link'
      case 'remote_video':
      case 'video':
        return 'mov'
    }
  },
  isViewableInBrowser(): boolean {
    switch (this.type) {
      case 'image':
        const ext = this.image?.split('.').pop()
        return (
          ext === 'png' ||
          ext === 'jpg' ||
          ext === 'jpeg' ||
          ext === 'gif' ||
          ext === 'webp' ||
          ext === 'svg' ||
          ext === 'avif' ||
          ext === 'bmp'
        )
      case 'remote_video':
      case 'video':
        return true
      case 'document':
      case 'iframe':
      case 'link':
        return false
    }
  },
}

export type ResourceDocument = RxDocument<ResourceDocType, ResourceDocMethods>

export type ResourceCollectionMethods = {
  test: () => void
}

export type ResourceCollection = RxCollection<ResourceDocType, ResourceDocMethods, ResourceCollectionMethods>
